import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Eye, BookOpen, Film } from 'lucide-react';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import MuxVideoPlayer from '../song-detail-page/components/MuxVideoPlayer';
import { fetchWarmUpById, incrementWarmUpViewCount } from '../../services/warmUpService';
import { useAuth } from '../../contexts/AuthContext';

const DIFFICULTY_STYLES = {
  Beginner: 'bg-green-100 text-green-700',
  Intermediate: 'bg-yellow-100 text-yellow-700',
  Advanced: 'bg-red-100 text-red-700',
};

const WarmUpDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperUser, userProfile } = useAuth();

  const [warmUp, setWarmUp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    loadWarmUp();
  }, [id]);

  const loadWarmUp = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchWarmUpById(id);
      setWarmUp(data);
      // Increment view count (non-blocking)
      incrementWarmUpViewCount(id);
    } catch (err) {
      setError(err?.message || 'Failed to load warm up.');
    } finally {
      setLoading(false);
    }
  };

  const getTalentName = () => {
    const t = warmUp?.talent;
    if (!t) return null;
    return t?.display_name || `${t?.first_name || ''} ${t?.last_name || ''}`?.trim() || null;
  };

  const isAdmin = isSuperUser?.() || userProfile?.role === 'editor';

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex items-center justify-center h-64"><LoadingSpinner /></div>
    </div>
  );

  if (error || !warmUp) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <Film className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
        <p className="text-foreground font-medium">{error || 'Warm up not found.'}</p>
        <button onClick={() => navigate('/warm-ups')} className="mt-4 text-primary hover:underline text-sm">
          ← Back to Warm Ups
        </button>
      </div>
    </div>
  );

  const talentName = getTalentName();
  const thumbnailUrl = warmUp?.mux_playback_id
    ? `https://image.mux.com/${warmUp?.mux_playback_id}/thumbnail.jpg?width=1280&height=720&fit_mode=smartcrop`
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>{warmUp?.title || 'Warm Up'} — RoleCall</title></Helmet>
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/warm-ups')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Warm Ups
        </button>

        {/* Video Player */}
        <div className="rounded-xl overflow-hidden bg-black mb-6">
          {warmUp?.mux_playback_id ? (
            <MuxVideoPlayer
              muxPlaybackId={warmUp?.mux_playback_id}
              videoId={warmUp?.mux_playback_id}
              title={warmUp?.title}
              thumbnailUrl={thumbnailUrl}
              propThumbnail={thumbnailUrl}
              onVideoComplete={() => {}}
              onWatchEvent={() => {}}
              className="w-full"
            />
          ) : (
            <div className="aspect-video flex items-center justify-center bg-muted">
              <div className="text-center">
                <Film className="w-12 h-12 text-muted-foreground mx-auto mb-2 opacity-40" />
                <p className="text-muted-foreground text-sm">Video not yet available</p>
              </div>
            </div>
          )}
        </div>

        {/* Title & Meta */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">{warmUp?.title}</h1>
          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
            {warmUp?.difficulty_level && (
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${DIFFICULTY_STYLES?.[warmUp?.difficulty_level] || 'bg-gray-100 text-gray-600'}`}>
                {warmUp?.difficulty_level}
              </span>
            )}
            {talentName && (
              <div className="flex items-center gap-2">
                {warmUp?.talent?.headshot_url ? (
                  <img src={warmUp?.talent?.headshot_url} alt={talentName} className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
                <span>{talentName}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              <span>{(warmUp?.view_count || 0) + 1} views</span>
            </div>
          </div>
        </div>

        {/* Description */}
        {warmUp?.description && (
          <div className="bg-card border border-border rounded-xl p-5 mb-4">
            <p className="text-foreground text-sm leading-relaxed">{warmUp?.description}</p>
          </div>
        )}

        {/* Instructions */}
        {warmUp?.instructions && (
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-primary" />
              <h2 className="font-semibold text-foreground text-sm">Instructions</h2>
            </div>
            <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{warmUp?.instructions}</div>
          </div>
        )}

        {/* Admin edit link */}
        {isAdmin && (
          <div className="mt-6 pt-4 border-t border-border">
            <button
              onClick={() => navigate('/warm-ups-management')}
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              ← Manage Warm Ups (Admin)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarmUpDetail;
